<h2 mat-dialog-title>{{ data.mode === 'edit' ? 'Editar Key Result' : 'Nuevo Key Result' }}</h2>
<div mat-dialog-content>
  <form [formGroup]="form" class="kr-form">
    <p class="context">{{ data.level_label }} · {{ data.objective_title }}</p>

    <div class="row-title-category">
      <mat-form-field appearance="fill">
        <mat-label>Título del Key Result</mat-label>
        <input matInput formControlName="title" />
      </mat-form-field>
      <mat-form-field appearance="fill">
        <mat-label>Categoría</mat-label>
        <mat-select formControlName="category">
          <mat-option [value]="null">Automática (IA)</mat-option>
          @for (category of categories; track category.value) {
            <mat-option [value]="category.value">{{ category.label }}</mat-option>
          }
        </mat-select>
      </mat-form-field>
    </div>

    <div class="row-thresholds">
      <mat-form-field appearance="fill">
        <mat-label>Valor objetivo</mat-label>
        <input matInput type="number" formControlName="good_target_value" />
        @if (unit_suffix()) {
          <span matSuffix class="unit-suffix">{{ unit_suffix() }}</span>
        }
        <mat-icon matSuffix [matTooltip]="'Stretch: el valor objetivo debería ser alcanzable ~50% de las veces.'">info</mat-icon>
      </mat-form-field>
      <mat-form-field appearance="fill">
        <mat-label>Valor aceptable</mat-label>
        <input matInput type="number" formControlName="acceptable_target_value" />
        @if (unit_suffix()) {
          <span matSuffix class="unit-suffix">{{ unit_suffix() }}</span>
        }
      </mat-form-field>
      <mat-form-field appearance="fill">
        <mat-label>Unidad</mat-label>
        <input matInput formControlName="unit" />
      </mat-form-field>
      <mat-form-field appearance="fill">
        <mat-label>Evaluación</mat-label>
        <mat-select formControlName="evaluation">
          @for (evaluation of evaluations; track evaluation.value) {
            <mat-option [value]="evaluation.value">{{ evaluation.label }}</mat-option>
          }
        </mat-select>
      </mat-form-field>
    </div>

    @if (data.mode === 'create') {
      <div class="row-metric">
        <mat-form-field appearance="fill">
          <mat-label>Asociar métrica</mat-label>
          <mat-select formControlName="metric_mode">
            <mat-option value="new">Es métrica nueva</mat-option>
            <mat-option value="existing">Usar métrica existente</mat-option>
          </mat-select>
        </mat-form-field>

        @if (metric_mode() === 'existing') {
          <button
            mat-stroked-button
            type="button"
            class="metric-selector-button"
            [class.metric-selector-button-empty]="selected_metric_title() === null"
            (click)="openMetricSelector()"
          >
            <mat-icon>bar_chart</mat-icon>
            <span>{{ selected_metric_label() }}</span>
          </button>
        } @else {
          <mat-form-field appearance="fill">
            <mat-label>Título de la métrica</mat-label>
            <input matInput formControlName="metric_title" placeholder="Nombre de la métrica (obligatorio)" />
          </mat-form-field>
        }
      </div>
    }

    @if (validation(); as result) {
      @if (!result.is_valid) {
        <div class="validation-feedback">
          @if (rejection_reason_label(); as reason_label) {
            <span class="rejection-reason">{{ reason_label }}</span>
          }
          <p class="feedback">{{ result.feedback }}</p>
          @if (result.suggestions.length > 0) {
            <div class="suggestions">
              @for (suggestion of result.suggestions; track suggestion) {
                <button mat-stroked-button type="button" class="suggestion-button" (click)="applySuggestion(suggestion)">
                  {{ suggestion }}
                </button>
              }
            </div>
          }
        </div>
      }
    }

    <section class="guide-qualities" aria-label="Cualidades de un buen Key Result">
      <p class="guide-qualities-title">Un buen Key Result</p>
      <ul>
        <li>
          <mat-icon>psychology</mat-icon>
          <span>Pregúntate: ¿qué conceptos mueven tu objetivo? ¿Y qué métricas mueven esos conceptos?</span>
        </li>
        <li>
          <mat-icon>sticky_note_2</mat-icon>
          <span
            ><strong>Freelisting:</strong> escribe tantas ideas como puedas, una por sticky note. Luego reordena, descarta y combina.
            Dedícale tiempo: las mejores ideas rara vez son las primeras y más obvias.</span
          >
        </li>
        <li>
          <mat-icon>flag</mat-icon>
          <span
            >Nunca un KR de <em>project completion</em>: debe medir un resultado (outcome), no un output ni una lista de tareas
            completadas.</span
          >
        </li>
        <li>
          <mat-icon>trending_up</mat-icon>
          <span>Difíciles, no imposibles. La confianza de conseguirlo debería partir de ~50% de probabilidad.</span>
        </li>
        <li>
          <mat-icon>speed</mat-icon>
          <span
            >Medibles en plazo corto. Si la métrica tarda meses en moverse, no sirve para iterar: busca una derivada que sea un buen
            indicador adelantado.</span
          >
        </li>
        <li>
          <mat-icon>compare_arrows</mat-icon>
          <span>Mide también los contraefectos de tu métrica (Andy Grove, <em>High Output Management</em>).</span>
        </li>
        <li>
          <mat-icon>balance</mat-icon>
          <span>Equilibrio usage / revenue / satisfaction (other no cuenta)</span>
        </li>
        <li>
          <mat-icon>timeline</mat-icon>
          <span>Ligado a una métrica con baseline; si aún no hay datos, el inicio del periodo queda vacío</span>
        </li>
      </ul>

      <div class="guide-examples">
        <div class="guide-examples-column guide-examples-good">
          <p class="guide-examples-heading">
            <mat-icon>check_circle</mat-icon>
            Ejemplos correctos
          </p>
          <ul>
            <li>«Llegar a 40% de weekly active users»</li>
            <li>«Bajar churn a 2%»</li>
            <li>«NPS ≥ 50»</li>
          </ul>
        </div>
        <div class="guide-examples-column guide-examples-bad">
          <p class="guide-examples-heading">
            <mat-icon>cancel</mat-icon>
            Ejemplos incorrectos
          </p>
          <ul>
            <li>«Lanzar la v2 del checkout»</li>
            <li>«Mejorar la app»</li>
            <li>«Hacer más marketing»</li>
          </ul>
        </div>
      </div>
    </section>

    <div class="guide-side-by-side">
      <div class="guide-panel">
        <p class="guide-panel-title">Notas</p>
        <ul>
          <li>
            <span class="guide-notes-label">Valor objetivo vs aceptable</span>
            El objetivo es stretch (~50%). El aceptable es el suelo: por debajo, el periodo no se considera bien encaminado.
          </li>
          <li>
            <span class="guide-notes-label">Métrica asociada</span>
            El KR apunta a una métrica real. Puedes reutilizar una existente o crear una nueva (tú eliges el nombre de la métrica).
          </li>
          <li>
            <span class="guide-notes-label">Título del KR vs título de la métrica</span>
            El KR es el resultado del periodo (con cifra); la métrica es lo que se mide en el tiempo. Ejemplo: KR «Al menos 1 KAM habrá
            creado 1 fabricación real» → métrica «Número de KAMs con fabricaciones creadas en últimos 30 días».
          </li>
        </ul>
      </div>

      <div class="guide-panel">
        <p class="guide-panel-title">Referencia teórica</p>
        <p class="guide-panel-copy">
          Seguimos a <strong>Christina Wodtke</strong> y su libro
          <a
            href="https://www.amazon.es/Radical-Focus-Achieving-Important-Objectives/dp/0996006087/ref=sr_1_1?crid=31CHAH8S7HLKO&dib=eyJ2IjoiMSJ9.EZMS5-2hzEHGwYUKcrwXWPhrPZenORiAzU0Ll9WNYRXcLPYUcTwI7ctq9PcPgu4xd9ypxqDRm-_TZOuGyPUNaDx7MghBQiLhtQfbmo2SmVKszfW1LEwavxTitIbV4QG8Y0JpqhKY-RQDo6DmWKXgkA._doG1GSrrq1nNnfeU6QRmKIwUD9-9Gh3S9cye2y7_Uo&dib_tag=se&keywords=radical+focus&qid=1787294033&sprefix=radical+fo%2Caps%2C172&sr=8-1"
            target="_blank"
            rel="noopener noreferrer"
            ><em>Radical Focus</em></a
          >: los Key Results son <strong>medibles</strong> y describen el <strong>outcome</strong> que prueba que el objective se está
          cumpliendo, no la lista de entregables.
        </p>
        <p class="guide-panel-copy">Si no puedes medirlo, no es un Key Result: es una tarea o un deseo.</p>
      </div>
    </div>

    @if (data.mode === 'edit') {
      <mat-form-field appearance="fill">
        <mat-label>Orden</mat-label>
        <input matInput type="number" formControlName="order" />
      </mat-form-field>
    }
  </form>
</div>
<div mat-dialog-actions align="end">
  <button mat-stroked-button type="button" [disabled]="saving()" (click)="close()">Cancelar</button>
  <button mat-flat-button color="accent" type="button" [disabled]="saving()" (click)="save()">
    {{ submit_label() }}
  </button>
</div>
